/**
 * Help content (/admin/help): write the help people see behind the "?" on each console page.
 *
 * App filter and page list (every page the apps know about, merged with stored help, so pages
 * without help show "Not written yet"), then per page: the page editor (summary, suggested
 * questions, help per tab) and the tours editor (steps, preview, show again).
 *
 * Access: the Admin role or the Help.Content.Manage right (checked by the route gate and the API).
 * Data: GET api/help/admin/pages; writes PUT/DELETE api/help/admin/pages/{pageKey} and
 * api/help/admin/tours/{tourKey}.
 */

import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { IconArrowLeft, IconBook2, IconHelp, IconRefresh, IconRoute, IconSearch } from '@tabler/icons-react';
import { LayoutContainer } from '@/components/ui/layout';
import { Alert, Badge, Button, Card, Group, SegmentedControl, Stack, Tabs, Text, TextInput, Title } from '@/components/ui';
import { DataTable, type DataTableColumn } from '@/components/advanced';
import { FadeIn } from '@/components/animations';
import { useHelpService, type HelpPageView, type HelpTourView } from '@actschurch/ccms-sdk';
import { AiOffHint, HELP_APPS, getHelpPageDef, helpKeys, helpPagesForApp, useHelpContext } from '@/components/help';
import { HelpPageEditor } from './HelpPageEditor';
import { HelpToursEditor } from './HelpToursEditor';
import { errorText } from './helpAdminUtils';

const ALL = 'all';

interface PageRow {
  id: string;
  pageKey: string;
  appKey: string;
  title: string;
  route: string;
  written: boolean;
  active: boolean;
  tabsWritten: number;
  tabsTotal: number;
  tours: number;
  autoStart: boolean;
  updatedAt?: string | null;
  updatedBy?: string | null;
  sortOrder: number;
}

function formatUpdated(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function HelpContentPage() {
  const help = useHelpService();
  const ctx = useHelpContext();
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const selectedKey = params.get('page');
  const appFilter = params.get('app') ?? ALL;
  const editorTab = params.get('tab') === 'tours' ? 'tours' : 'page';
  const [search, setSearch] = useState('');

  const setParam = useCallback(
    (key: string, value: string | null) => {
      const next = new URLSearchParams(params);
      if (value) next.set(key, value);
      else next.delete(key);
      if (key === 'page') next.delete('tab');
      setParams(next);
    },
    [params, setParams]
  );

  const stored = useQuery({
    queryKey: helpKeys.adminPages(null),
    queryFn: () => help.getHelpAdminPages(null),
    retry: 1,
  });

  const storedByKey = useMemo(() => new Map((stored.data ?? []).map((p) => [p.pageKey, p] as const)), [stored.data]);

  const rows: PageRow[] = useMemo(() => {
    const keys = new Set<string>();
    const out: PageRow[] = [];
    const add = (pageKey: string) => {
      if (keys.has(pageKey)) return;
      keys.add(pageKey);
      const def = getHelpPageDef(pageKey);
      const page = storedByKey.get(pageKey);
      const tabsTotal = Math.max(def?.tabs.length ?? 0, page?.tabs?.length ?? 0);
      out.push({
        id: pageKey,
        pageKey,
        appKey: page?.appKey || def?.appKey || pageKey.split('.')[0],
        title: page?.title || def?.title || pageKey,
        route: page?.route || def?.route || '',
        written: !!page,
        active: page?.isActive ?? true,
        tabsWritten: (page?.tabs ?? []).filter((t) => !!t.bodyHtml).length,
        tabsTotal,
        tours: page?.tours?.length ?? 0,
        autoStart: !!page?.tours?.some((t) => t.isActive && t.autoStart),
        updatedAt: page?.updatedAt,
        updatedBy: page?.updatedBy,
        sortOrder: page?.sortOrder ?? def?.sortOrder ?? 999,
      });
    };
    helpPagesForApp(null).forEach((d) => add(d.pageKey));
    (stored.data ?? []).forEach((p) => add(p.pageKey));
    const q = search.trim().toLowerCase();
    return out
      .filter((r) => appFilter === ALL || r.appKey === appFilter)
      .filter((r) => !q || r.title.toLowerCase().includes(q) || r.pageKey.toLowerCase().includes(q) || r.route.toLowerCase().includes(q))
      .sort((a, b) => a.appKey.localeCompare(b.appKey) || a.sortOrder - b.sortOrder || a.title.localeCompare(b.title));
  }, [storedByKey, stored.data, appFilter, search]);

  const appOptions = useMemo(() => {
    const known = new Set(HELP_APPS.map((a) => a.appKey));
    const extra = Array.from(new Set((stored.data ?? []).map((p) => p.appKey).filter((k) => k && !known.has(k))));
    return [{ value: ALL, label: 'All apps' }, ...HELP_APPS.map((a) => ({ value: a.appKey, label: a.title })), ...extra.map((k) => ({ value: k, label: k }))];
  }, [stored.data]);

  const counts = useMemo(() => {
    const all = rows.length;
    const written = rows.filter((r) => r.written).length;
    return { all, written };
  }, [rows]);

  // ---------------------------------------------------------------- cache updates after saves

  const replaceStored = (page: HelpPageView) =>
    queryClient.setQueryData<HelpPageView[]>(helpKeys.adminPages(null), (old) => {
      const list = old ?? [];
      const prev = list.find((p) => p.pageKey === page.pageKey);
      const merged: HelpPageView = { ...page, tours: page.tours?.length ? page.tours : prev?.tours ?? [] };
      return prev ? list.map((p) => (p.pageKey === page.pageKey ? merged : p)) : [...list, merged];
    });

  const replaceTour = (pageKey: string, tour: HelpTourView) =>
    queryClient.setQueryData<HelpPageView[]>(helpKeys.adminPages(null), (old) =>
      (old ?? []).map((p) =>
        p.pageKey === pageKey ? { ...p, tours: p.tours.some((t) => t.tourKey === tour.tourKey) ? p.tours.map((t) => (t.tourKey === tour.tourKey ? tour : t)) : [...p.tours, tour] } : p
      )
    );

  const removeTour = (pageKey: string, tourKey: string) =>
    queryClient.setQueryData<HelpPageView[]>(helpKeys.adminPages(null), (old) => (old ?? []).map((p) => (p.pageKey === pageKey ? { ...p, tours: p.tours.filter((t) => t.tourKey !== tourKey) } : p)));

  const removePage = (pageKey: string) => queryClient.setQueryData<HelpPageView[]>(helpKeys.adminPages(null), (old) => (old ?? []).filter((p) => p.pageKey !== pageKey));

  // ---------------------------------------------------------------- list columns

  const columns: DataTableColumn<PageRow>[] = [
    {
      key: 'title',
      label: 'Page',
      render: (_v, r) => (
        <Stack gap={0}>
          <Text size="sm" fw={600}>
            {r.title}
          </Text>
          <Text size="xs" c="dimmed">
            {r.pageKey}
          </Text>
        </Stack>
      ),
    },
    { key: 'route', label: 'Route', render: (_v, r) => <Text size="xs">{r.route || 'Not set'}</Text> },
    {
      key: 'written',
      label: 'Help',
      render: (_v, r) =>
        !r.written ? (
          <Badge color="orange" variant="light" styles={{ root: { textTransform: 'none' } }}>
            Not written yet
          </Badge>
        ) : !r.active ? (
          <Badge color="gray" variant="light" styles={{ root: { textTransform: 'none' } }}>
            Inactive
          </Badge>
        ) : (
          <Badge color="teal" variant="light" styles={{ root: { textTransform: 'none' } }}>
            Written
          </Badge>
        ),
    },
    { key: 'tabsWritten', label: 'Tabs', render: (_v, r) => <Text size="sm">{r.tabsTotal === 0 ? 'None' : `${r.tabsWritten} of ${r.tabsTotal}`}</Text> },
    {
      key: 'tours',
      label: 'Tours',
      render: (_v, r) => (
        <Group gap={4} wrap="nowrap">
          <Text size="sm">{r.tours}</Text>
          {r.autoStart && (
            <Badge size="xs" variant="light" styles={{ root: { textTransform: 'none' } }}>
              Auto
            </Badge>
          )}
        </Group>
      ),
    },
    {
      key: 'updatedAt',
      label: 'Updated',
      render: (_v, r) => (
        <Text size="xs" c="dimmed">
          {formatUpdated(r.updatedAt)}
          {r.updatedBy ? ` by ${r.updatedBy}` : ''}
        </Text>
      ),
    },
  ];

  // ---------------------------------------------------------------- editor view

  if (selectedKey) {
    const def = getHelpPageDef(selectedKey);
    const page = storedByKey.get(selectedKey) ?? null;
    const title = page?.title || def?.title || selectedKey;
    return (
      <LayoutContainer>
        <FadeIn>
          <Stack gap="lg">
            <Stack gap="xs">
              <Group>
                <Button variant="subtle" size="compact-sm" leftSection={<IconArrowLeft size={14} />} onClick={() => setParam('page', null)}>
                  All pages
                </Button>
              </Group>
              <Group justify="space-between" wrap="wrap" align="flex-start" gap="sm">
                <Stack gap={0}>
                  <Title order={2}>{title}</Title>
                  <Text size="sm" c="dimmed">
                    {selectedKey}
                    {def?.menuPath ? ` · ${def.menuPath}` : ''}
                  </Text>
                </Stack>
                <Badge color={page ? (page.isActive ? 'teal' : 'gray') : 'orange'} variant="light" size="lg" styles={{ root: { textTransform: 'none' } }}>
                  {page ? (page.isActive ? 'Written' : 'Inactive') : 'Not written yet'}
                </Badge>
              </Group>
            </Stack>

            {!def && (
              <Alert color="orange" title="Not in the app">
                No page with this key is marked in the console, so tours cannot point at its elements. The help still shows to Ask AI.
              </Alert>
            )}
            {stored.error && (
              <Alert color="red" title="Stored help could not be loaded">
                {errorText(stored.error, 'Try again.')} Saving now would replace what is stored.
              </Alert>
            )}

            <Tabs value={editorTab} onChange={(v) => setParam('tab', v === 'tours' ? 'tours' : null)}>
              <Tabs.List>
                <Tabs.Tab value="page" leftSection={<IconBook2 size={14} />}>
                  Page and tabs
                </Tabs.Tab>
                <Tabs.Tab value="tours" leftSection={<IconRoute size={14} />}>
                  Tours ({page?.tours?.length ?? 0})
                </Tabs.Tab>
              </Tabs.List>
              <Tabs.Panel value="page" pt="md">
                {stored.isLoading ? (
                  <Text size="sm" c="dimmed">
                    Loading the stored help.
                  </Text>
                ) : (
                  <HelpPageEditor
                    pageKey={selectedKey}
                    page={page}
                    def={def}
                    onSaved={replaceStored}
                    onDeleted={() => {
                      removePage(selectedKey);
                      void stored.refetch();
                    }}
                  />
                )}
              </Tabs.Panel>
              <Tabs.Panel value="tours" pt="md">
                {editorTab === 'tours' && !stored.isLoading && (
                  <HelpToursEditor
                    pageKey={selectedKey}
                    page={page}
                    def={def}
                    onTourSaved={(tour) => replaceTour(selectedKey, tour)}
                    onTourDeleted={(tourKey) => removeTour(selectedKey, tourKey)}
                  />
                )}
              </Tabs.Panel>
            </Tabs>
          </Stack>
        </FadeIn>
      </LayoutContainer>
    );
  }

  // ---------------------------------------------------------------- list view

  return (
    <LayoutContainer>
      <FadeIn>
        <Stack gap="lg">
          <Group justify="space-between" wrap="wrap" align="flex-start" gap="sm">
            <Stack gap={0}>
              <Group gap="xs">
                <IconHelp size={26} />
                <Title order={2}>Help content</Title>
              </Group>
              <Text size="sm" c="dimmed">
                Write the help, tab help and tours people see behind the ? on each page.
              </Text>
            </Stack>
            <Button variant="light" leftSection={<IconRefresh size={16} />} onClick={() => stored.refetch()} loading={stored.isFetching && !stored.isLoading}>
              Refresh
            </Button>
          </Group>

          {ctx && ctx.ask.loaded && !ctx.ask.enabled && <AiOffHint isAdmin={ctx.isAdmin} message={ctx.ask.message} />}

          {stored.error && (
            <Alert color="red" title="Could not load the stored help">
              {errorText(stored.error, 'Try again in a moment.')}
            </Alert>
          )}

          <Card withBorder radius="md" padding="md">
            <Group justify="space-between" wrap="wrap" gap="sm">
              <Group gap="sm" wrap="wrap">
                <SegmentedControl value={appFilter} onChange={(v) => setParam('app', v === ALL ? null : v)} data={appOptions} />
                <TextInput
                  placeholder="Search pages"
                  aria-label="Search pages"
                  leftSection={<IconSearch size={14} />}
                  value={search}
                  onChange={(e) => setSearch(e.currentTarget.value)}
                  w={240}
                />
              </Group>
              <Text size="sm" c="dimmed">
                {counts.written} of {counts.all} pages written
              </Text>
            </Group>
          </Card>

          <Card withBorder radius="md" padding={0}>
            <DataTable<PageRow>
              data={rows}
              columns={columns}
              loading={stored.isLoading}
              onRowClick={(r) => setParam('page', r.pageKey)}
              highlightOnHover
              enableInternalSearch={false}
              emptyMessage={search ? 'No pages match your search.' : 'No pages yet.'}
            />
          </Card>
        </Stack>
      </FadeIn>
    </LayoutContainer>
  );
}
